import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Form, Spinner, Col, Row } from 'react-bootstrap';
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import debounce from 'lodash/debounce';
import "./Style.css";

export default function Transfer({ showTransfer, closeModal, refreshPage }) {
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [fromData, setFromData] = useState({
    warehouse_id: "",
    category_id: "",
    sub_category_id: "",
    product_id: "",
    personel_id: "",
    quantity: "",
    quantity_transferred: 0,
    batch_numbers: "",
  });

  const [toData, setToData] = useState({
    destination_warehouse_id: "",
    to_category_id: "",
    to_sub_category_id: "",
    to_product_id: "",
    to_personel_id: "",
    to_quantity: "",
    to_batch_numbers: "",
  });

  const [fromCategories, setFromCategories] = useState([]);
  const [fromSubCategories, setFromSubCategories] = useState([]);
  const [fromProducts, setFromProducts] = useState([]);
  const [toCategories, setToCategories] = useState([]);
  const [toSubCategories, setToSubCategories] = useState([]);
  const [toProducts, setToProducts] = useState([]);
  const [warehouse, setWarehouse] = useState([]);

  const [loadingWarehouse, setLoadingWarehouse] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [loadingSubCategory, setLoadingSubCategory] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingWarehouse(true);
      try {
        const response = await axiosInstance.get('/user/warehouses/get_warehouse');
        setWarehouse(response.data.data.warehouse);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        toast.error("Failed to load warehouses.");
      } finally {
        setLoadingWarehouse(false);
      }
    };
    fetchData();
  }, []);

  // Debounced function to fetch products
  const fetchProducts = useCallback(
    debounce(async (subCategoryId, warehouseId, setProducts, type, updateData, fieldPrefix) => {
      if (!subCategoryId || !warehouseId) return;
      setLoadingProduct(true);
      try {
        const response = await axiosInstance.get(
          `/user/transfer/products_by_sub_id/${subCategoryId}?warehouse_id=${warehouseId}`
        );
        setProducts(response.data.data.products);
        updateData((prev) => ({
          ...prev,
          [`${fieldPrefix}product_id`]: "",
          [`${fieldPrefix}quantity`]: "",
          [`${fieldPrefix}batch_numbers`]: "",
          [`${fieldPrefix}personel_id`]: "",
        }));
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products.");
      } finally {
        setLoadingProduct(false);
      }
    }, 300),
    []
  );

  const handleChange = async (e, type) => {
    const { name, value } = e.target;
    const updateData = type === 'from' ? setFromData : setToData;
    const setCategories = type === 'from' ? setFromCategories : setToCategories;
    const setSubCategories = type === 'from' ? setFromSubCategories : setToSubCategories;
    const setProducts = type === 'from' ? setFromProducts : setToProducts;
    const products = type === 'from' ? fromProducts : toProducts;
    const fieldPrefix = type === 'from' ? '' : 'to_';

    updateData((prev) => ({ ...prev, [name]: value }));

    if (['warehouse_id', 'destination_warehouse_id'].includes(name)) {
      setLoadingCategory(true);
      try {
        const response = await axiosInstance.get(`/user/transfer/category_by_warehouse/${value}`);
        setCategories(response.data.data.categories);
        setSubCategories([]);
        setProducts([]);
        updateData((prev) => ({
          ...prev,
          [`${fieldPrefix}category_id`]: "",
          [`${fieldPrefix}sub_category_id`]: "",
          [`${fieldPrefix}product_id`]: "",
          [`${fieldPrefix}quantity`]: "",
          [`${fieldPrefix}batch_numbers`]: "",
          [`${fieldPrefix}personel_id`]: "",
        }));
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories.");
      } finally {
        setLoadingCategory(false);
      }
    }

    if (['category_id', 'to_category_id'].includes(name)) {
      setLoadingSubCategory(true);
      try {
        const warehouseId = type === 'from' ? fromData.warehouse_id : toData.destination_warehouse_id;
        const response = await axiosInstance.get(
          `/user/transfer/subcategories_category_id/${value}?warehouse_id=${warehouseId}`
        );
        setSubCategories(response.data.data.subcategories);
        setProducts([]);
        updateData((prev) => ({
          ...prev,
          [`${fieldPrefix}sub_category_id`]: "",
          [`${fieldPrefix}product_id`]: "",
          [`${fieldPrefix}quantity`]: "",
          [`${fieldPrefix}batch_numbers`]: "",
          [`${fieldPrefix}personel_id`]: "",
        }));
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        toast.error("Failed to load subcategories.");
      } finally {
        setLoadingSubCategory(false);
      }
    }

    if (['sub_category_id', 'to_sub_category_id'].includes(name)) {
      const warehouseId = type === 'from' ? fromData.warehouse_id : toData.destination_warehouse_id;
      fetchProducts(value, warehouseId, setProducts, type, updateData, fieldPrefix);
    }

    if (['product_id', 'to_product_id'].includes(name)) {
      const selectedProduct = products.find((prod) => prod.id.toString() === value.toString());
      if (selectedProduct) {
        updateData((prev) => ({
          ...prev,
          [`${fieldPrefix}personel_id`]: selectedProduct.full_name || "N/A",
          [`${fieldPrefix}quantity`]: selectedProduct.quantity || "",
          [`${fieldPrefix}batch_numbers`]: selectedProduct.batch_numbers || "",
        }));
      }
    }

    if (name === 'quantity_transferred') {
      const qty = parseInt(value) || 0;
      if (qty > parseInt(fromData.quantity)) {
        toast.error("Transferred quantity cannot exceed available quantity.");
        updateData((prev) => ({ ...prev, quantity_transferred: prev.quantity }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationError({});

    try {
      const transferData = {
        warehouse_id: fromData.warehouse_id,
        category_id: fromData.category_id,
        sub_category_id: fromData.sub_category_id,
        product_id: fromData.product_id,
        from_batch_numbers: fromData.batch_numbers,
        personel_id: fromData.personel_id === "N/A" ? null : fromData.personel_id,
        quantity: fromData.quantity,
        quantity_transferred: parseInt(fromData.quantity_transferred),
        destination_warehouse_id: toData.destination_warehouse_id,
        to_category_id: toData.to_category_id,
        to_sub_category_id: toData.to_sub_category_id,
        to_product_id: toData.to_product_id,
        to_batch_numbers: toData.to_batch_numbers,
        to_personel_id: toData.to_personel_id === "N/A" ? null : toData.to_personel_id,
        to_quantity: toData.to_quantity,
      };

      if (!transferData.quantity_transferred || transferData.quantity_transferred <= 0) {
        toast.error("Please enter a valid quantity to transfer.");
        setIsLoading(false);
        return;
      }

      if (transferData.quantity_transferred > parseInt(transferData.quantity)) {
        toast.error("Transferred quantity cannot exceed available quantity.");
        setIsLoading(false);
        return;
      }

      await axiosInstance.post('/user/transfer/save_transfer', transferData);
      toast.success("Transfer saved successfully!");
      refreshPage();
      handleClose();
    } catch (error) {
      if (error.response && error.response.data) {
        setValidationError(error.response.data.message || {});
        toast.error(error.response.data.message || "Failed to save transfer.");
      } else {
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFromData({
      warehouse_id: "",
      category_id: "",
      sub_category_id: "",
      product_id: "",
      personel_id: "",
      quantity: "",
      quantity_transferred: 0,
      batch_numbers: "",
    });
    setToData({
      destination_warehouse_id: "",
      to_category_id: "",
      to_sub_category_id: "",
      to_product_id: "",
      to_personel_id: "",
      to_quantity: "",
      to_batch_numbers: "",
    });
    setFromCategories([]);
    setFromSubCategories([]);
    setFromProducts([]);
    setToCategories([]);
    setToSubCategories([]);
    setToProducts([]);
    setValidationError({});
    closeModal();
  };

  return (
    <div className="CreateCSP">
      <Modal show={showTransfer} onHide={handleClose} dialogClassName="modal-lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Transfer Product</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-4 p-3" style={{ border: '1px dotted rgba(0, 0, 0, 0.5)' }}>
              <h5 className="text-center text-info mb-4">From</h5>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="warehouseInput">
                    <Form.Label>Warehouse</Form.Label>
                    <select
                      className="form-control"
                      name="warehouse_id"
                      value={fromData.warehouse_id}
                      onChange={(e) => handleChange(e, 'from')}
                      required
                    >
                      <option value="">-- Select Warehouse --</option>
                      {loadingWarehouse ? (
                        <option>Loading...</option>
                      ) : (
                        warehouse.map((ware) => (
                          <option key={ware.id} value={ware.id}>
                            {ware.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.warehouse_id && (
                      <small className="text-danger">{validationError.warehouse_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="categoryInput">
                    <Form.Label>Category</Form.Label>
                    <select
                      className="form-control"
                      name="category_id"
                      value={fromData.category_id}
                      onChange={(e) => handleChange(e, 'from')}
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {loadingCategory ? (
                        <option>Loading...</option>
                      ) : (
                        fromCategories.map((cat) => (
                          <option key={cat.category_id} value={cat.category_id}>
                            {cat.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.category_id && (
                      <small className="text-danger">{validationError.category_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="sub_categoryInput">
                    <Form.Label>Sub Category</Form.Label>
                    <select
                      className="form-control"
                      name="sub_category_id"
                      value={fromData.sub_category_id}
                      onChange={(e) => handleChange(e, 'from')}
                      required
                    >
                      <option value="">-- Select Sub Category --</option>
                      {loadingSubCategory ? (
                        <option>Loading...</option>
                      ) : (
                        fromSubCategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.sub_category_id && (
                      <small className="text-danger">{validationError.sub_category_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="productInput">
                    <Form.Label>Product</Form.Label>
                    <select
                      className="form-control"
                      name="product_id"
                      value={fromData.product_id}
                      onChange={(e) => handleChange(e, 'from')}
                      required
                    >
                      <option value="">-- Select Product --</option>
                      {loadingProduct ? (
                        <option>Loading...</option>
                      ) : fromProducts.length > 0 ? (
                        fromProducts.map((prod) => (
                          <option key={prod.id} value={prod.id}>
                            {prod.product_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No products available</option>
                      )}
                    </select>
                    {validationError.product_id && (
                      <small className="text-danger">{validationError.product_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="personelInput">
                    <Form.Label>Personnel</Form.Label>
                    <Form.Control
                      type="text"
                      name="personel_id"
                      value={fromData.personel_id}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="quantityInput">
                    <Form.Label>Available Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      name="quantity"
                      value={fromData.quantity}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="batchNumbersInput">
                    <Form.Label>Batch Numbers</Form.Label>
                    <Form.Control
                      type="text"
                      name="batch_numbers"
                      value={fromData.batch_numbers}
                      onChange={(e) => handleChange(e, 'from')}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="quantityTransferredInput">
                    <Form.Label><b>Quantity to Transfer</b></Form.Label>
                    <Form.Control
                      type="number"
                      name="quantity_transferred"
                      value={fromData.quantity_transferred}
                      onChange={(e) => handleChange(e, 'from')}
                      min="0"
                      max={fromData.quantity}
                      required
                    />
                    {validationError.quantity_transferred && (
                      <small className="text-danger">{validationError.quantity_transferred[0]}</small>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="mb-4 p-3" style={{ border: '1px dotted rgba(0, 0, 0, 0.5)' }}>
              <h5 className="text-center text-success mb-4">To</h5>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="destination_warehouseInput">
                    <Form.Label>Destination Warehouse</Form.Label>
                    <select
                      className="form-control"
                      name="destination_warehouse_id"
                      value={toData.destination_warehouse_id}
                      onChange={(e) => handleChange(e, 'to')}
                      required
                    >
                      <option value="">-- Select Warehouse --</option>
                      {loadingWarehouse ? (
                        <option>Loading...</option>
                      ) : (
                        warehouse.map((ware) => (
                          <option key={ware.id} value={ware.id}>
                            {ware.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.destination_warehouse_id && (
                      <small className="text-danger">{validationError.destination_warehouse_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="toCategoryInput">
                    <Form.Label>Category</Form.Label>
                    <select
                      className="form-control"
                      name="to_category_id"
                      value={toData.to_category_id}
                      onChange={(e) => handleChange(e, 'to')}
                      required
                    >
                      <option value="">-- Select Category --</option>
                      {loadingCategory ? (
                        <option>Loading...</option>
                      ) : (
                        toCategories.map((cat) => (
                          <option key={cat.category_id} value={cat.category_id}>
                            {cat.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.to_category_id && (
                      <small className="text-danger">{validationError.to_category_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="toSubCategoryInput">
                    <Form.Label>Sub Category</Form.Label>
                    <select
                      className="form-control"
                      name="to_sub_category_id"
                      value={toData.to_sub_category_id}
                      onChange={(e) => handleChange(e, 'to')}
                      required
                    >
                      <option value="">-- Select Sub Category --</option>
                      {loadingSubCategory ? (
                        <option>Loading...</option>
                      ) : (
                        toSubCategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))
                      )}
                    </select>
                    {validationError.to_sub_category_id && (
                      <small className="text-danger">{validationError.to_sub_category_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="toProductInput">
                    <Form.Label>Product</Form.Label>
                    <select
                      className="form-control"
                      name="to_product_id"
                      value={toData.to_product_id}
                      onChange={(e) => handleChange(e, 'to')}
                      required
                    >
                      <option value="">-- Select Product --</option>
                      {loadingProduct ? (
                        <option>Loading...</option>
                      ) : toProducts.length > 0 ? (
                        toProducts.map((prod) => (
                          <option key={prod.id} value={prod.id}>
                            {prod.product_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No products available</option>
                      )}
                    </select>
                    {validationError.to_product_id && (
                      <small className="text-danger">{validationError.to_product_id[0]}</small>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="toPersonelInput">
                    <Form.Label>Personnel</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_personel_id"
                      value={toData.to_personel_id}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="toQuantityInput">
                    <Form.Label>Current Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      name="to_quantity"
                      value={toData.to_quantity}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group controlId="toBatchNumbersInput">
                    <Form.Label>Batch Numbers</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_batch_numbers"
                      value={toData.to_batch_numbers}
                      onChange={(e) => handleChange(e, 'to')}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Transfer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}